/**
 * TeamSection — assigned team members for a CAP.
 *
 * Compliance staff pick from the org's onboarded team (users with a CAP role
 * via user_app_roles, scoped to the CAP's department + org-wide compliance
 * users). The picker is the same shape across the module: dropdown driven by
 * /api/pcap/eligible-users.
 *
 * Two roles per assignment:
 *   - contributor  can edit assigned sections
 *   - approver     can sign off the four checkpoints
 */

import React, { useEffect, useState, useCallback } from 'react';
import SectionCard from './SectionCard';
import Pill from './Pill';

const API = process.env.REACT_APP_API_URL;

export default function TeamSection({ data, onRefresh }) {
  const cap = data?.cap;
  const assignments = (data?.assignments || []).filter(a => a.user_id);

  const [eligible, setEligible] = useState([]);
  const [pickedUserId, setPickedUserId] = useState('');
  const [pickedRole, setPickedRole] = useState('contributor');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadEligible = useCallback(() => {
    const params = new URLSearchParams();
    if (cap?.department_id) params.set('department_id', cap.department_id);
    fetch(`${API}/api/pcap/eligible-users?${params.toString()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setEligible(d.users || []); })
      .catch(() => {});
  }, [cap?.department_id]);

  useEffect(() => { loadEligible(); }, [loadEligible]);

  const assignedUserIds = new Set(assignments.map(a => a.user_id));
  const pickable = eligible.filter(u => !assignedUserIds.has(u.id));

  const userById = (id) => eligible.find(u => u.id === id);

  const add = async (e) => {
    e.preventDefault();
    if (!pickedUserId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: pickedUserId, role: pickedRole }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not add');
      setPickedUserId('');
      setPickedRole('contributor');
      onRefresh?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (assignmentId) => {
    if (!window.confirm('Remove this person from the CAP?')) return;
    try {
      const res = await fetch(`${API}/api/pcap/assignments/${assignmentId}/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not remove');
      onRefresh?.();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <SectionCard
      title="Team"
      hint="Who can work on this CAP. Pulled from your onboarded team. Approvers can sign off the checkpoints below."
    >
      {error && (
        <div className="mb-3 rounded-lg bg-red-50 ring-1 ring-red-200 p-2.5 text-sm text-red-800">{error}</div>
      )}

      {/* Existing members */}
      {assignments.length > 0 && (
        <ul className="divide-y divide-slate-100 mb-4">
          {assignments.map(a => {
            const u = userById(a.user_id);
            return (
              <li key={a.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  <Pill tone={a.role === 'approver'
                    ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
                    : 'bg-slate-100 text-slate-700 ring-slate-200'}>
                    {a.role}
                  </Pill>
                  <span className="text-sm text-slate-800 truncate">
                    {u ? u.name : `(user ${a.user_id.slice(0, 8)}…)`}
                  </span>
                  {u?.email && <span className="text-[12px] text-slate-500 truncate">{u.email}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => revoke(a.id)}
                  className="text-[12px] text-slate-500 hover:text-red-700 underline underline-offset-2"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Add form */}
      {pickable.length === 0 && assignments.length === 0 && (
        <p className="text-sm text-slate-400 italic">
          No team members onboarded with CAP access yet. Add them through the onboarding wizard or from /people.
        </p>
      )}
      {pickable.length > 0 && (
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_160px_auto] gap-2">
          <select
            value={pickedUserId}
            onChange={e => setPickedUserId(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          >
            <option value="">Pick a teammate…</option>
            {pickable.map(u => (
              <option key={u.id} value={u.id}>
                {u.name} {u.email ? `· ${u.email}` : ''} {u.cap_role ? `· ${u.cap_role}` : ''}
              </option>
            ))}
          </select>
          <select
            value={pickedRole}
            onChange={e => setPickedRole(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          >
            <option value="contributor">Contributor</option>
            <option value="approver">Approver</option>
          </select>
          <button
            type="submit"
            disabled={busy || !pickedUserId}
            className="px-3.5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition disabled:opacity-50"
          >
            {busy ? 'Adding…' : 'Add'}
          </button>
        </form>
      )}
    </SectionCard>
  );
}
